import { inject, Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { getUserContextClaims, UserContextClaims } from '@deighton/shared-ui/core';
import { MutationResult } from '@deighton/shared-ui/services';
import { DocumentNode, GraphQLFormattedError } from 'graphql';
import { lastValueFrom, Observable, throwError } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  AssignRoleToUserResponse,
  BulkAssignRoleToUsersResponse,
  BulkRevokeRoleFromUsersResponse,
  GetRoleByIdResponse,
  GetRolesResponse,
  GetUsersForRoleManagementResponse,
  RoleByIdDto,
  BulkRoleAssignmentResult,
  RevokeRoleFromUserResponse,
  RoleDto,
  RoleType,
  SortEnumType,
  UserAssignmentStatusFilter,
  UserForRoleManagementDto,
  UserListSortField,
  UsersPageQueryOptions,
} from './role-assignment.interface';
import { RoleOperationError, throwIfGraphQLErrors } from '../identity-graphql-error';
import { RoleCategory } from './role-category.util';

/** Result shape returned by the paged users query. */
export interface UsersPageResult {
  nodes: Array<UserForRoleManagementDto>;
  hasNextPage: boolean;
  endCursor: string | undefined;
}

/** Result shape returned by a single page of the GetRoles query. */
interface RolesPageResult {
  nodes: Array<RoleDto>;
  hasNextPage: boolean;
  endCursor: string | undefined;
}

/** Shared user node selection for paginated GetUsersForRoleManagement queries. */
const ROLE_MANAGEMENT_USER_NODE_FIELDS: string = `
        id
        userName
        email
        displayName
        roleAssignments {
          roleId
          roleName
          isAdmin
          roleType
          effectiveScope
        }`;

/** Cache key for dynamically built GetUsersForRoleManagement queries (inline where/order avoids HC0047). */
type UsersPageQueryCacheKey = string;

/** Cached GetUsersForRoleManagement query documents keyed by filter/sort shape. */
const usersPageQueryCache: Map<UsersPageQueryCacheKey, DocumentNode> = new Map<
  UsersPageQueryCacheKey,
  DocumentNode
>();

/** Scalar variables required by a cached users query (pagination vars added at call time). */
interface UsersPageQueryVariables {
  search?: string;
  roleId?: string;
}

/** Resolved filter/sort shape used to build or look up a users query document. */
interface UsersPageQueryShape {
  sortField: UserListSortField;
  sortDirection: SortEnumType;
  hasSearch: boolean;
  roleCategories: ReadonlyArray<RoleCategory>;
  assignmentStatus: UserAssignmentStatusFilter;
  /** When set, restricts the users query to assignees of this role id. */
  roleId?: string;
}

/** Builds the inline HotChocolate order clause (not a variable — avoids HC0047). */
function buildUsersOrderClause(sortField: UserListSortField, sortDirection: SortEnumType): string {
  switch (sortField) {
    case 'email':
      return `[{ email: ${sortDirection} }]`;
    case 'role':
      return `[{ sortRoleName: ${sortDirection} }]`;
    case 'name':
    default:
      return `[{ displayName: ${sortDirection} }]`;
  }
}

/** Builds an inline where fragment for a single UI role category chip. */
function buildRoleCategoryWhereFragment(category: RoleCategory): string {
  switch (category) {
    case 'governance':
      return `{ roles: { some: { isAdmin: { eq: true } } } }`;
    case 'platform':
      return `{ roles: { some: { and: [{ roleType: { eq: ${RoleType.BuiltIn} } }, { isAdmin: { eq: false } }] } } }`;
    case 'custom':
      return `{ roles: { some: { roleType: { neq: ${RoleType.BuiltIn} } } } }`;
  }
}

/** Builds inline where fragments combined with `and` when multiple filters are active. */
function buildUsersWhereFragments(shape: UsersPageQueryShape): Array<string> {
  const fragments: Array<string> = new Array<string>();

  if (shape.hasSearch) {
    fragments.push(`{
        or: [
          { displayName: { contains: $search } }
          { userName: { contains: $search } }
          { email: { contains: $search } }
        ]
      }`);
  }

  if (shape.roleCategories.length === 1) {
    fragments.push(buildRoleCategoryWhereFragment(shape.roleCategories[0]));
  } else if (shape.roleCategories.length > 1) {
    const categoryFragments: string = shape.roleCategories
      .map((category: RoleCategory): string => buildRoleCategoryWhereFragment(category))
      .join(' ');
    fragments.push(`{ or: [${categoryFragments}] }`);
  }

  if (shape.assignmentStatus === 'hasRole') {
    fragments.push(`{ roles: { any: true } }`);
  } else if (shape.assignmentStatus === 'noRole') {
    fragments.push(`{ roles: { any: false } }`);
  }

  return fragments;
}

/** Returns a stable cache key for the given query shape. */
function buildUsersPageQueryCacheKey(shape: UsersPageQueryShape): UsersPageQueryCacheKey {
  return [
    shape.sortField,
    shape.sortDirection,
    shape.hasSearch ? 'search' : '',
    shape.roleCategories.join('+') || '',
    shape.assignmentStatus,
    shape.roleId ?? '',
  ].join(':');
}

/** Returns or creates a GetUsersForRoleManagement query with inline where/order and scalar variables only. */
function getUsersForRoleMgmtQuery(shape: UsersPageQueryShape): DocumentNode {
  const cacheKey: UsersPageQueryCacheKey = buildUsersPageQueryCacheKey(shape);
  const cached: DocumentNode | undefined = usersPageQueryCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const variableDeclarations: Array<string> = [
    '$tenantId: UUID!',
    '$environmentId: UUID!',
    '$first: Int',
    '$after: String',
  ];
  if (shape.hasSearch) {
    variableDeclarations.push('$search: String');
  }
  if (shape.roleId) {
    variableDeclarations.push('$roleId: UUID!');
  }

  const whereFragments: Array<string> = buildUsersWhereFragments(shape);
  let whereClause: string = '';
  if (whereFragments.length === 1) {
    whereClause = `where: ${whereFragments[0]}`;
  } else if (whereFragments.length > 1) {
    whereClause = `where: { and: [${whereFragments.join(' ')}] }`;
  }

  const orderClause: string = buildUsersOrderClause(shape.sortField, shape.sortDirection);
  const getUsersArgs: Array<string> = [
    'tenantId: $tenantId',
    'environmentId: $environmentId',
    ...(shape.roleId ? ['roleId: $roleId'] : []),
    'first: $first',
    'after: $after',
    ...(whereClause.length > 0 ? [whereClause] : []),
    `order: ${orderClause}`,
  ];

  const query: DocumentNode = gql`
    query GetUsersForRoleManagementWithAssignments(${variableDeclarations.join(', ')}) {
      GetUsersForRoleManagement(${getUsersArgs.join(', ')}) {
        nodes {
          ${ROLE_MANAGEMENT_USER_NODE_FIELDS}
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  `;

  usersPageQueryCache.set(cacheKey, query);
  return query;
}

/** Resolves UI options into a query shape and scalar variables for GetUsersForRoleManagement. */
function resolveUsersPageQuery(options?: UsersPageQueryOptions): {
  shape: UsersPageQueryShape;
  variables: UsersPageQueryVariables;
} {
  const trimmedSearch: string = (options?.searchTerm ?? '').trim();
  const roleCategories: ReadonlyArray<RoleCategory> = options?.roleCategories ?? [];
  const roleId: string | undefined = options?.roleId?.trim() || undefined;
  const shape: UsersPageQueryShape = {
    sortField: options?.sortField ?? 'name',
    sortDirection: (options?.sortDirection ?? 'asc').toUpperCase() as SortEnumType,
    hasSearch: trimmedSearch.length > 0,
    roleCategories,
    assignmentStatus: options?.assignmentStatus ?? 'all',
    roleId,
  };

  const variables: UsersPageQueryVariables = {};
  if (shape.hasSearch) {
    variables.search = trimmedSearch;
  }
  if (shape.roleId) {
    variables.roleId = shape.roleId;
  }

  return { shape, variables };
}

/** Fetches a single role with granted permissions for the role details drawer. */
const GET_ROLE_BY_ID: DocumentNode = gql`
  query GetRoleById($roleId: UUID!) {
    GetRoleById(roleId: $roleId) {
      id
      name
      description
      isGlobal
      isAdmin
      roleType
      effectiveScope
      createdOnUtc
      modifiedOnUtc
      createdBy
      modifiedBy
      permissions {
        id
        area
        target
        action
        enforcementKey
        displayName
        description
      }
    }
  }
`;

/**
 * Fetches all available platform roles.
 * Uses cursor-based pagination to collect every page.
 */
const GET_ROLES: DocumentNode = gql`
  query GetRoles($first: Int, $after: String, $tenantId: UUID!, $environmentId: UUID!) {
    GetRoles(first: $first, after: $after) {
      nodes {
        id
        name
        description
        isGlobal
        isAdmin
        roleType
        effectiveScope
        assignedUserCount(tenantId: $tenantId, environmentId: $environmentId)
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`;

/** Assigns a single role to a single user by their GUIDs. */
const ASSIGN_ROLE_TO_USER: DocumentNode = gql`
  mutation AssignRoleToUser($userId: UUID!, $roleId: UUID!, $tenantId: UUID!, $environmentId: UUID!) {
    AssignRoleToUser(userId: $userId, roleId: $roleId, tenantId: $tenantId, environmentId: $environmentId)
  }
`;

/** Revokes a single role from a single user by their GUIDs. */
const REVOKE_ROLE_FROM_USER: DocumentNode = gql`
  mutation RevokeRoleFromUser($userId: UUID!, $roleId: UUID!, $tenantId: UUID!, $environmentId: UUID!) {
    RevokeRoleFromUser(userId: $userId, roleId: $roleId, tenantId: $tenantId, environmentId: $environmentId)
  }
`;

/** Assigns a single role to multiple users in one operation. */
const BULK_ASSIGN_ROLE_TO_USERS: DocumentNode = gql`
  mutation BulkAssignRoleToUsers(
    $roleId: UUID!
    $userIds: [UUID!]!
    $tenantId: UUID!
    $environmentId: UUID!
  ) {
    BulkAssignRoleToUsers(
      roleId: $roleId
      userIds: $userIds
      tenantId: $tenantId
      environmentId: $environmentId
    ) {
      successCount
      failures {
        userId
        reason
      }
    }
  }
`;

/** Revokes a single role from multiple users in one operation. */
const BULK_REVOKE_ROLE_FROM_USERS: DocumentNode = gql`
  mutation BulkRevokeRoleFromUsers(
    $roleId: UUID!
    $userIds: [UUID!]!
    $tenantId: UUID!
    $environmentId: UUID!
  ) {
    BulkRevokeRoleFromUsers(
      roleId: $roleId
      userIds: $userIds
      tenantId: $tenantId
      environmentId: $environmentId
    ) {
      successCount
      failures {
        userId
        reason
      }
    }
  }
`;

/**
 * Service for managing platform role assignments.
 *
 * All query/mutation names are derived directly from the backend GraphQL schema
 * defined in Tenant.Web.Graphql.Queries.Identity and Tenant.Web.Graphql.Mutations.Identity.
 */
@Injectable()
export class RoleAssignmentService {
  /** Number of users fetched per page in `getUsersPage`. Matches CAR app LIST_PAGE_SIZE. */
  public static readonly PAGE_SIZE: number = 20;

  /** Apollo client for GQL communication. */
  private readonly apollo: Apollo = inject(Apollo);

  /**
   * Fetches all users in the current management context with their role assignments.
   * Auto-paginates through all pages.
   * @returns Promise of users from {@link GetUsersForRoleManagementResponse}.
   */
  public async getTenantUsers(): Promise<Array<UserForRoleManagementDto>> {
    const allUsers: Array<UserForRoleManagementDto> = [];
    let hasNextPage: boolean = true;
    let after: string | undefined;

    while (hasNextPage) {
      const page: UsersPageResult = await lastValueFrom(this.getUsersPage(after));
      allUsers.push(...page.nodes);
      hasNextPage = page.hasNextPage;
      after = page.endCursor;
    }

    return allUsers;
  }

  /**
   * Fetches a single page of users with their role assignments for the session management context.
   * @param after - Cursor for the next page; omit or pass undefined for the first page.
   * @param options - Search, sort, and filter options for the query.
   * @returns Observable resolving to the page nodes, hasNextPage, and the next cursor.
   */
  public getUsersPage(after?: string, options?: UsersPageQueryOptions): Observable<UsersPageResult> {
    const sessionContext: UserContextClaims | null = getUserContextClaims();
    if (!sessionContext) {
      return throwError(
        (): Error =>
          new Error(
            '[RoleAssignmentService] Session tenant/environment context is required for role management.'
          )
      );
    }

    const { shape, variables: queryVariables } = resolveUsersPageQuery(options);

    return this.apollo
      .query<GetUsersForRoleManagementResponse>({
        query: getUsersForRoleMgmtQuery(shape),
        variables: {
          tenantId: sessionContext.tenantId,
          environmentId: sessionContext.environmentId,
          first: options?.first ?? RoleAssignmentService.PAGE_SIZE,
          after,
          ...queryVariables,
        },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(
          (response: {
            data?: GetUsersForRoleManagementResponse;
            errors?: ReadonlyArray<GraphQLFormattedError>;
          }): UsersPageResult => {
            throwIfGraphQLErrors(response.errors, 'Role operation failed.');

            const connection = response.data?.GetUsersForRoleManagement;
            if (!connection) {
              throw new RoleOperationError(false, 'GetUsersForRoleManagement returned no data.');
            }

            return {
              nodes: connection.nodes ?? new Array<UserForRoleManagementDto>(),
              hasNextPage: connection.pageInfo?.hasNextPage ?? false,
              endCursor: connection.pageInfo?.endCursor ?? undefined,
            };
          }
        )
      );
  }

  /**
   * Fetches all users matching the query options, auto-paginating through every page.
   * Used by the roles filter UI so callers do not need load-more wiring.
   */
  public async getUsersMatching(options?: UsersPageQueryOptions): Promise<Array<UserForRoleManagementDto>> {
    const allUsers: Array<UserForRoleManagementDto> = new Array<UserForRoleManagementDto>();
    let hasNextPage: boolean = true;
    let after: string | undefined;

    while (hasNextPage) {
      const page: UsersPageResult = await lastValueFrom(this.getUsersPage(after, options));
      allUsers.push(...page.nodes);
      hasNextPage = page.hasNextPage;
      after = page.endCursor;
    }

    return allUsers;
  }

  /**
   * Fetches all available platform roles for the session management context.
   * Auto-paginates through all pages.
   * @returns Promise of all role DTOs.
   */
  public async getAllRoles(): Promise<Array<RoleDto>> {
    const sessionContext: UserContextClaims | null = getUserContextClaims();
    if (!sessionContext) {
      throw new Error(
        '[RoleAssignmentService] Session tenant/environment context is required for role management.'
      );
    }

    const allRoles: Array<RoleDto> = [];
    let hasNextPage: boolean = true;
    let after: string | undefined;

    while (hasNextPage) {
      const page: RolesPageResult = await lastValueFrom(
        this.apollo
          .query<GetRolesResponse>({
            query: GET_ROLES,
            variables: {
              first: 50,
              after,
              tenantId: sessionContext.tenantId,
              environmentId: sessionContext.environmentId,
            },
            fetchPolicy: 'network-only',
          })
          .pipe(
            map(
              (response: {
                data?: GetRolesResponse;
                errors?: ReadonlyArray<GraphQLFormattedError>;
              }): RolesPageResult => {
                throwIfGraphQLErrors(response.errors, 'Role operation failed.');

                const connection = response.data?.GetRoles;
                if (!connection) {
                  throw new RoleOperationError(false, 'GetRoles returned no data.');
                }

                return {
                  nodes: connection.nodes ?? [],
                  hasNextPage: connection.pageInfo?.hasNextPage ?? false,
                  endCursor: connection.pageInfo?.endCursor ?? undefined,
                };
              }
            )
          )
      );

      allRoles.push(...page.nodes);
      hasNextPage = page.hasNextPage;
      after = page.endCursor;
    }

    return allRoles;
  }

  /**
   * Fetches all users assigned to a role in the session management context.
   * @param roleId - Role GUID filter.
   * @returns All users with an assignment to the role (auto-paginated).
   */
  public async getUsersWithRole(roleId: string): Promise<Array<UserForRoleManagementDto>> {
    const allUsers: Array<UserForRoleManagementDto> = [];
    let hasNextPage: boolean = true;
    let after: string | undefined;

    while (hasNextPage) {
      const page: UsersPageResult = await lastValueFrom(
        this.getUsersPage(after, { roleId, sortField: 'name', sortDirection: 'asc' })
      );
      allUsers.push(...page.nodes);
      hasNextPage = page.hasNextPage;
      after = page.endCursor;
    }

    return allUsers;
  }

  /**
   * Fetches a role by id with its granted permissions.
   * @param roleId - The role's GUID.
   * @returns Observable of the role details, or an error when the query fails.
   */
  public getRoleById(roleId: string): Observable<RoleByIdDto> {
    return this.apollo
      .query<GetRoleByIdResponse>({
        query: GET_ROLE_BY_ID,
        variables: { roleId },
        fetchPolicy: 'network-only',
      })
      .pipe(
        map(
          (response: {
            data?: GetRoleByIdResponse;
            errors?: ReadonlyArray<GraphQLFormattedError>;
          }): RoleByIdDto => {
            throwIfGraphQLErrors(response.errors, 'Role operation failed.');

            const role: RoleByIdDto | null | undefined = response.data?.GetRoleById;
            if (!role) {
              throw new RoleOperationError(false, 'GetRoleById returned no data.');
            }

            return role;
          }
        )
      );
  }

  /**
   * Assigns a role to a single user.
   * @param userId - The user's GUID.
   * @param roleId - The role's GUID.
   * @returns Observable boolean — true on success.
   */
  public assignRoleToUser(userId: string, roleId: string): Observable<boolean> {
    const sessionContext: UserContextClaims = this.requireSessionContext();
    return this.apollo
      .mutate<AssignRoleToUserResponse>({
        mutation: ASSIGN_ROLE_TO_USER,
        variables: {
          userId,
          roleId,
          tenantId: sessionContext.tenantId,
          environmentId: sessionContext.environmentId,
        },
      })
      .pipe(
        map((result: MutationResult<AssignRoleToUserResponse>): boolean => {
          throwIfGraphQLErrors(result.errors, 'Role operation failed.');
          const succeeded: boolean = result.data?.AssignRoleToUser ?? false;
          this.throwIfNotSuccessful(succeeded, 'Role assignment');
          return true;
        })
      );
  }

  /**
   * Revokes a role from a single user.
   * @param userId - The user's GUID.
   * @param roleId - The role's GUID.
   * @returns Observable boolean — true on success.
   */
  public revokeRoleFromUser(userId: string, roleId: string): Observable<boolean> {
    const sessionContext: UserContextClaims = this.requireSessionContext();
    return this.apollo
      .mutate<RevokeRoleFromUserResponse>({
        mutation: REVOKE_ROLE_FROM_USER,
        variables: {
          userId,
          roleId,
          tenantId: sessionContext.tenantId,
          environmentId: sessionContext.environmentId,
        },
      })
      .pipe(
        map((result: MutationResult<RevokeRoleFromUserResponse>): boolean => {
          throwIfGraphQLErrors(result.errors, 'Role operation failed.');
          const succeeded: boolean = result.data?.RevokeRoleFromUser ?? false;
          this.throwIfNotSuccessful(succeeded, 'Role revocation');
          return true;
        })
      );
  }

  /**
   * Throws a {@link RoleOperationError} when a mutation completes without GraphQL errors but
   * returns a false success flag — a soft backend rejection that would otherwise be treated
   * as success by callers that only check for observable completion.
   * @param succeeded - The boolean success flag from the mutation payload.
   * @param operationLabel - Human-readable label used in the error message.
   */
  private throwIfNotSuccessful(succeeded: boolean, operationLabel: string): void {
    if (succeeded) {
      return;
    }
    throw new RoleOperationError(false, `${operationLabel} was rejected by the server.`);
  }

  /**
   * Bulk assigns a role to multiple users.
   * @param roleId - The role's GUID.
   * @param userIds - Array of user GUIDs.
   * @returns Observable bulk result with success count and any failures.
   */
  public bulkAssignRoleToUsers(roleId: string, userIds: Array<string>): Observable<BulkRoleAssignmentResult> {
    const sessionContext: UserContextClaims = this.requireSessionContext();
    return this.apollo
      .mutate<BulkAssignRoleToUsersResponse>({
        mutation: BULK_ASSIGN_ROLE_TO_USERS,
        variables: {
          roleId,
          userIds,
          tenantId: sessionContext.tenantId,
          environmentId: sessionContext.environmentId,
        },
      })
      .pipe(
        map((result: MutationResult<BulkAssignRoleToUsersResponse>): BulkRoleAssignmentResult => {
          throwIfGraphQLErrors(result.errors, 'Role operation failed.');
          return result.data?.BulkAssignRoleToUsers ?? { successCount: 0, failures: [] };
        })
      );
  }

  /**
   * Bulk revokes a role from multiple users.
   * @param roleId - The role's GUID.
   * @param userIds - Array of user GUIDs.
   * @returns Observable bulk result with success count and any failures.
   */
  public bulkRevokeRoleFromUsers(
    roleId: string,
    userIds: Array<string>
  ): Observable<BulkRoleAssignmentResult> {
    const sessionContext: UserContextClaims = this.requireSessionContext();
    return this.apollo
      .mutate<BulkRevokeRoleFromUsersResponse>({
        mutation: BULK_REVOKE_ROLE_FROM_USERS,
        variables: {
          roleId,
          userIds,
          tenantId: sessionContext.tenantId,
          environmentId: sessionContext.environmentId,
        },
      })
      .pipe(
        map((result: MutationResult<BulkRevokeRoleFromUsersResponse>): BulkRoleAssignmentResult => {
          throwIfGraphQLErrors(result.errors, 'Role operation failed.');
          return result.data?.BulkRevokeRoleFromUsers ?? { successCount: 0, failures: [] };
        })
      );
  }

  /**
   * Returns session tenant/environment context or throws when unavailable.
   * @returns Non-null session context for role management operations.
   */
  private requireSessionContext(): UserContextClaims {
    const sessionContext: UserContextClaims | null = getUserContextClaims();
    if (!sessionContext) {
      throw new Error(
        '[RoleAssignmentService] Session tenant/environment context is required for role management.'
      );
    }
    return sessionContext;
  }
}
